import React, { useEffect, useState } from 'react'
import {
  Button,
  Checkbox,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  Box,
  SelectChangeEvent,
  Chip,
} from '@mui/material'
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom'
import DownloadIcon from '@mui/icons-material/Download'
import UploadIcon from '@mui/icons-material/Upload'
import Link from '@mui/material/Link'
import LoadingButton from '@mui/lab/LoadingButton'
import { useTranslation } from 'react-i18next'
import { convertContactOrderStatusTextToText } from '~/src/utils/convert'
import { formatDate } from '~/src/utils/formatDate'
import { formatMoney } from '~/src/utils/formatMoney'
import { IRequestOrderResponse } from '~/src/repositories/Order/models/Order'
import { StatusType } from '~/src/constants/requestOrder'
import { orderRepository } from '~/src/repositories'
import { useMessage, useScroll, useCurrency } from '~/src/hooks'
import { stringToNumber } from '~/src/libs'
import { MessageModal } from '~/src/components/organisms/ContainerOrder/Message/MessageModal'
import { convertSupplyType, ISupplyType } from '~/src/utils/convert'
import { TablePaginationActions } from '~/src/components/organisms'
import { FileImportDialog } from '~/src/components/organisms/FileImportDialog/FileImportDialog'

interface IContactOrderItem {
  id: string
  contactNo: string
  refNo: string
  controlNo: string
  categoryName: string
  nameProduct: string
  orderDetail: string
  message: string
  status: string
  manufacture: string
  nameCar: string
  modelVehicle: string
  year: string
  originalRefNo: string
  originalRemark: string
  price: string
  finalPrice: string
  isDeposited: string
  createDate: number
  updateDate: number
  supplyType: ISupplyType
  companyName: string
  isUnread: boolean
}

interface IColumn {
  id:
    | 'checked'
    | 'contactNo'
    | 'refNo'
    | 'controlNo'
    | 'categoryName'
    | 'nameProduct'
    | 'orderDetail'
    | 'message'
    | 'status'
    | 'supplyType'
    | 'manufacture'
    | 'nameCar'
    | 'modelVehicle'
    | 'year'
    | 'originalRefNo'
    | 'originalRemark'
    | 'price'
    | 'finalPrice'
    | 'isDeposited'
    | 'createDate'
    | 'updateDate'
    | 'companyName'
  label: string | React.ReactNode
  minWidth?: number
  align?: 'right'
  render: (item: IContactOrderItem, index: number) => React.ReactNode
}
interface IGetOrderParams {
  keyword?: string
  userId?: number
  limit?: number
  offset?: number
  includeCanceled?: boolean
  orderedAtBegin?: string
  orderedAtEnd?: string
  detailBaseUrl?: string
}

interface IContactOrderProps {
  data?: IContactOrderItem[]
  pagination?: IRequestOrderResponse['pagination']
  onNextPages: (_params: IGetOrderParams) => void
  params: IGetOrderParams
  reloadList: () => void
}

export const ContactOrder: React.FC<IContactOrderProps> = ({
  data,
  pagination,
  onNextPages,
  params,
  reloadList
}): JSX.Element => {
  const { t } = useTranslation()
  const location = useLocation()

  const scrollPosition = useScroll()

  const [selected, setSelected] = React.useState<string[]>([])
  const [page, setPage] = useState((pagination?.currentPage ?? 0) - 1)
  const [rowsPerPage, setRowsPerPage] = useState(pagination?.limitValue)
  const isSelected = (name: string) => selected.indexOf(name) !== -1
  const [status, setStatus] = useState('')
  const { handleOpenWithErrorMessage, handleOpenWithSuccessMessage } = useMessage()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalId, setModalId] = useState('')
  const [isExportExcelLoading, setIsExportExcelLoading] = useState(false)
  const [isUpdateFileImportOpen, setUpdateFileImportOpen] = useState(false)
  const [isEngineFileImportOpen, setEngineFileImportOpen] = useState(false)
  const [isCkdPartFileImportOpen, setCkdPartFileImportOpen] = useState(false)
  const [isClosedReload, setClosedReload] = useState(false)
  const navigate = useNavigate()
  const { baseCurrencyUnit, unitPosition } = useCurrency()


  // reload時にpagerが更新されないので追加
  useEffect(() => {
    if (pagination?.currentPage !== page) {
      // @ts-ignore
      setPage(pagination?.currentPage - 1)
      setRowsPerPage(pagination?.limitValue)
    }
  }, [pagination])

  /**
   * handleOpen
   * @param id
   */
  const handleOpen = (id: string) => {
    setIsModalOpen(true)
    setModalId(id)
  }
  /**
   * handleClose
   */
  const handleClose = () => {
    setIsModalOpen(false)
  }
  /**
   * handleSelectAllClick
   * @param event
   */
  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = data?.map((n) => n.id)
      setSelected(newSelected ?? [])
      return
    }
    setSelected([])
  }
  /**
   * handleChangeStatus
   * @param e
   */
  const handleChangeStatus = (e: SelectChangeEvent) => {
    setStatus(e.target.value)
  }
  /**
   * getKeyByValue
   * @param object
   * @param value
   */
  const getKeyByValue = (object: Record<string, unknown>, value: unknown) => {
    return Object.keys(object).find((key) => object[key] === value)
  }
  /**
   * handleBatchOperation
   */
  const handleBatchOperation = async () => {
    try {
      const selectedIds = selected.map((id) => Number(id))
      const res = await orderRepository.patchRequestOrderStatus({
        ids: selectedIds,
        status: Number(status),
      })
      handleOpenWithSuccessMessage('更新しました')
    } catch (e: any) {
      console.error(e)
      handleOpenWithErrorMessage(e.message)
    } finally {
      window.location.reload()
    }
  }
  /**
   * handleClick
   * @param event
   * @param id
   */
  const handleClick = (event: React.ChangeEvent<HTMLInputElement>, id: string) => {
    const selectedIndex = selected.indexOf(id)

    let newSelected: string[] = []
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id)
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1))
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1))
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      )
    }
    setSelected(newSelected)
  }

  const handleExportExcel = async (params) => {
    setIsExportExcelLoading(true)
    const { data } = await orderRepository.exportExcel(params)
    if (data) {
      const url = URL.createObjectURL(new Blob([data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'contact.xlsx')
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
    }
    setIsExportExcelLoading(false)
  }

  const handleUpdateFileUpload = async (file) => {
    let messages = []
    let errors = []
    try {
      const result = await orderRepository.importUpdateFile(file)
      setClosedReload(true)
      messages = [
        t('contact_order.imported_count', {count: result.data.requestOrderIdList.length}),
      ]
    } catch (e) {
      if (e.response.status == 422) {
        errors = Object.values(e.response.data.errors)
      } else {
        errors = [e.message]
      }
    }
    return {
      messages: messages,
      errors: errors,
    }
  }

  const handleEngineFileUpload = async (file) => {
    let messages = []
    let errors = []
    try {
      const result = await orderRepository.importEngineFile(file)
      setClosedReload(true)
      messages = [
        t('contact_order.imported_count', {count: result.data.requestOrderIdList.length}),
      ]
    } catch (e) {
      if (e.response.status == 422) {
        errors = Object.values(e.response.data.errors)
      } else {
        errors = [e.message]
      }
    }
    return {
      messages: messages,
      errors: errors,
    }
  }

  const handleCkdPartFileUpload = async (file) => {
    let messages = []
    let errors = []
    try {
      const result = await orderRepository.importCkdPartFile(file)
      setClosedReload(true)
      messages = [
        t('contact_order.imported_count', {count: result.data.requestOrderIdList.length}),
      ]
    } catch (e) {
      if (e.response.status == 422) {
        errors = Object.values(e.response.data.errors.flat())
      }
    }
    return {
      messages: messages,
      errors: errors,
    }
  }

  const closeUpdateFileUploadDialog = async () => {
    if (isClosedReload) {
      setClosedReload(false)
      await reloadList()
    }
    setUpdateFileImportOpen(false)
  }

  const closeEngineFileUploadDialog = async () => {
    if (isClosedReload) {
      setClosedReload(false)
      await reloadList()
    }
    setEngineFileImportOpen(false)
  }

  const closeCkdPartFileUploadDialog = async () => {
    if (isClosedReload) {
      setClosedReload(false)
      await reloadList()
    }
    setCkdPartFileImportOpen(false)
  }

  const columns: IColumn[] = [
    {
      id: 'checked',
      label: (
        <Checkbox
          size={'small'}
          color="primary"
          indeterminate={selected.length > 0 && selected.length < (data?.length ?? 0)}
          checked={(data?.length ?? 0) > 0 && selected.length === data?.length}
          onChange={handleSelectAllClick}
          style={{
            padding: 1,
            marginLeft: 5,
            marginRight: 5,
            marginTop: '-4px',
          }}
          inputProps={{
            'aria-label': 'select all desserts',
          }}
        />
      ),
      minWidth: 100,
      render: ({ id }, index) => (
        <Checkbox
          size={'small'}
          onChange={(event) => handleClick(event, id)}
          color="primary"
          checked={isSelected(id)}
          inputProps={{
            'aria-labelledby': `table-member-checkbox-${index}`,
          }}
        />
      ),
    },
    {
      id: 'contactNo',
      label: `${t('contact_order.contact_no')}`,
      minWidth: 150,
      render: ({ id, isUnread }) => (
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.15px',
            color: 'rgba(0, 0, 0, 0.87)',
            fontWeight: isUnread ? 'bold' : 'normal',
            '&:before': {
              content: '""',
              display: 'inline-block',
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: isUnread ? '#F23D3D' : 'transparent',
              verticalAlign: 'top',
              marginTop: '5px',
              marginRight: '10px',
            },
          }}
        >
          {id}
        </Typography>
      ),
    },
    {
      id: 'controlNo',
      label: `${t('contact_order.control_no')}`,
      minWidth: 150,
      render: ({ controlNo, isUnread }) => (
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.15px',
            color: 'rgba(0, 0, 0, 0.87)',
            fontWeight: isUnread ? 'bold' : 'normal',
          }}
        >
          {controlNo}
        </Typography>
      ),
    },
    {
      id: 'companyName',
      label: `${t('contact_order.company_name')}`,
      minWidth: 150,
      render: ({ companyName, isUnread }) => (
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.15px',
            color: 'rgba(0, 0, 0, 0.87)',
            fontWeight: isUnread ? 'bold' : 'normal',
          }}
        >
          {companyName}
        </Typography>
      ),
    },
    {
      id: 'categoryName',
      label: `${t('contact_order.category_name')}`,
      minWidth: 150,
      render: ({ categoryName, isUnread }) => (
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.15px',
            fontWeight: isUnread ? 'bold' : 'normal',
          }}
        >
          {categoryName}
        </Typography>
      ),
    },
    {
      id: 'nameProduct',
      label: `${t('contact_order.product_name')}`,
      minWidth: 400,
      render: ({ id, nameProduct, isUnread }) => (
        <Box
          variant="body2"
          sx={{
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.15px',
            fontWeight: isUnread ? 'bold' : 'normal',
          }}
        >
          <RouterLink
            className="router-link"
            to={`/admin/order/contact-order/detail/${id}`}
            state={{ prevLocationState: { ...location, scrollY: scrollPosition } }}
          >
            {nameProduct}
          </RouterLink>
        </Box>
      ),
    },
    {
      id: 'orderDetail',
      label: `${t('contact_order.order_details')}`,
      minWidth: 200,
      render: ({ orderDetail, isUnread }) => (
        <Box
          variant="body2"
          sx={{
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.15px',
            fontWeight: isUnread ? 'bold' : 'normal',
          }}
        >
          <Box dangerouslySetInnerHTML={{ __html: orderDetail }} />
        </Box>
      ),
    },
    {
      id: 'message',
      label: `${t('contact_order.message')}`,
      minWidth: 200,
      render: ({ id, isUnread }) => (
        <Box
          variant="body2"
          sx={{
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.15px',
            fontWeight: isUnread ? 'bold' : 'normal',
          }}
        >
          <Link onClick={() => handleOpen(id)} href="#" underline="always">
            {t('contact_order.latest_history')}
          </Link>
        </Box>
      ),
    },
    {
      id: 'status',
      label: `${t('contact_order.status')}`,
      minWidth: 160,
      render: ({ status, isUnread }) => {
        return status !== '-' ? (
          <Chip
            label={convertContactOrderStatusTextToText(status)}
            variant="outlined"
            sx={{
              fontFamily: 'Roboto',
              fontStyle: 'normal',
              fontSize: '14px',
              lineHeight: '20px',
              letterSpacing: '0.15px',
              height: '24px',
              color: 'rgba(0, 0, 0, 0.87)',
              fontWeight: isUnread ? 'bold' : 'normal',
            }}
          />
        ) : (
          <Typography
            variant="body2"
            sx={{
              fontFamily: 'Roboto',
              fontStyle: 'normal',
              fontSize: '14px',
              lineHeight: '20px',
              letterSpacing: '0.15px',
              color: 'rgba(0, 0, 0, 0.87)',
              fontWeight: isUnread ? 'bold' : 'normal',
            }}
          >
            {convertContactOrderStatusTextToText(status)}
          </Typography>
        )
      },
    },
    {
      id: 'year',
      label: `${t('contact_order.year')}`,
      minWidth: 120,
      render: ({ year, isUnread }) => (
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.15px',
            color: 'rgba(0, 0, 0, 0.87)',
            fontWeight: isUnread ? 'bold' : 'normal',
          }}
        >
          {year}
        </Typography>
      ),
    },
    {
      id: 'originalRefNo',
      label: `${t('contact_order.original_ref_no')}`,
      minWidth: 150,
      render: ({ originalRefNo, isUnread }) => (
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.15px',
            color: 'rgba(0, 0, 0, 0.87)',
            fontWeight: isUnread ? 'bold' : 'normal',
          }}
        >
          {originalRefNo}
        </Typography>
      ),
    },
    {
      id: 'originalRemark',
      label: `${t('contact_order.original_remarks')}`,
      minWidth: 200,
      render: ({ originalRemark, isUnread }) => (
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.15px',
            color: 'rgba(0, 0, 0, 0.87)',
            fontWeight: isUnread ? 'bold' : 'normal',
          }}
        >
          {originalRemark}
        </Typography>
      ),
    },
    {
      // 調達区分
      id: 'supplyType',
      label: `${t('contact_order.supply_type')}`,
      minWidth: 120,
      render: ({ supplyType, isUnread }) => (
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.15px',
            color: 'rgba(0, 0, 0, 0.87)',
            fontWeight: isUnread ? 'bold' : 'normal',
          }}
        >
          {convertSupplyType(supplyType)}
        </Typography>
      ),
    },
    {
      id: 'price',
      label: `${t('contact_order.price')}`,
      minWidth: 120,
      render: ({ price, isUnread }) => (
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.15px',
            color: 'rgba(0, 0, 0, 0.87)',
            fontWeight: isUnread ? 'bold' : 'normal',
          }}
        >
          {formatMoney(stringToNumber(price), baseCurrencyUnit, unitPosition)}
        </Typography>
      ),
    },
    {
      id: 'finalPrice',
      label: `${t('contact_order.final_price')}`,
      minWidth: 120,
      render: ({ finalPrice, isUnread }) => (
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.15px',
            color: 'rgba(0, 0, 0, 0.87)',
            fontWeight: isUnread ? 'bold' : 'normal',
          }}
        >
          {formatMoney(stringToNumber(finalPrice), baseCurrencyUnit, unitPosition)}
        </Typography>
      ),
    },
    {
      id: 'isDeposited',
      label: `${t('contact_order.deposit_status')}`,
      minWidth: 120,
      render: ({ isDeposited, isUnread }) => (
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.15px',
            color: 'rgba(0, 0, 0, 0.87)',
            fontWeight: isUnread ? 'bold' : 'normal',
          }}
        >
          {isDeposited ? t('contact_order.deposit_payment_done') : t('contact_order.deposit_payment_yet')}
        </Typography>
      ),
    },
    {
      id: 'createDate',
      label: `${t('contact_order.data_create_date')}`,
      minWidth: 150,
      render: ({ createDate, isUnread }) => (
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.15px',
            color: 'rgba(0, 0, 0, 0.87)',
            fontWeight: isUnread ? 'bold' : 'normal',
          }}
        >
          {createDate ? formatDate(createDate) : '-'}
        </Typography>
      ),
    },
    {
      id: 'updateDate',
      label: `${t('contact_order.data_update_date')}`,
      minWidth: 150,
      render: ({ updateDate, isUnread }) => (
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.15px',
            color: 'rgba(0, 0, 0, 0.87)',
            fontWeight: isUnread ? 'bold' : 'normal',
          }}
        >
          {updateDate ? formatDate(updateDate) : '-'}
        </Typography>
      ),
    },
  ]
  /**
   * handleChangePage
   * @param event
   * @param newPage
   */
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage)
    onNextPages({ limit: rowsPerPage, offset: newPage + 1 })
  }
  /**
   * handleChangeRowsPerPage
   * @param event
   */
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value)
    setPage(0)
  }

  return (
    <>
      <Paper sx={{ width: '100%', mb: 2 }}>
        <Stack direction={'row'} spacing={1} padding={'20px'}>
          <Select
            value={status}
            displayEmpty
            renderValue={(value) =>
              getKeyByValue(StatusType, Number(value)) || t('contact_order.bulk_operation')
            }
            inputProps={{ 'aria-label': 'Without label' }}
            size={'small'}
            sx={{
              opacity: !status ? '0.6' : '1',
              width: '220px',
              fontSize: '16px',
              lineHeight: '24px',
              fontStyle: 'normal',
              background: 'white',
              height: '40px',
            }}
            onChange={handleChangeStatus}
          >
            <MenuItem value={'100'}>PENDING</MenuItem>
            <MenuItem value={'300'}>NEGOTIATION</MenuItem>
            <MenuItem value={'400'}>CANCEL</MenuItem>
            <MenuItem value={'500'}>ACCEPTED</MenuItem>
            <MenuItem value={'600'}>IN PROGRESS</MenuItem>
            <MenuItem value={'1000'}>CLOSED</MenuItem>
          </Select>
          <Button
            variant="contained"
            size={'small'}
            onClick={handleBatchOperation}
            disabled={!status || !selected.length}
          >
            <Typography
              sx={{
                fontStyle: 'normal',
                fontSize: 14,
                lineHeight: '24px',
                fontWeight: 500,
              }}
            >
              {t('contact_order.be_utilized')}
            </Typography>
          </Button>
          <LoadingButton
            type="submit"
            loading={isExportExcelLoading}
            startIcon={<></>}
            loadingPosition="start"
            size={'medium'}
            variant="outlined"
            sx={{
              minWidth: isExportExcelLoading ? '230px' : '200px',
              ml: '25px',
              height: '40px',
              padding: '7px 22px 7px 19px',
              background: '#FFFFFF',
            }}
            onClick={() => handleExportExcel(params)}
          >
            <DownloadIcon sx={{ width: '22px', height: '25px', marginRight: '8px' }} />
            {t('contact_order.excel_export')}
          </LoadingButton>
          <Button
            variant="outlined"
            startIcon={
              <UploadIcon fontSize={'small'} />
            }
            onClick={() => setUpdateFileImportOpen(true)}
          >
            {t('contact_order.excel_import')}
          </Button>
          <Button
            variant="outlined"
            startIcon={
              <UploadIcon fontSize={'small'} />
            }
            onClick={() => setEngineFileImportOpen(true)}
          >
            {t('contact_order.order_import_engine')}
          </Button>
          <Button
            variant="outlined"
            startIcon={
              <UploadIcon fontSize={'small'} />
            }
            onClick={() => setCkdPartFileImportOpen(true)}
          >
            {t('contact_order.order_import_ckd_part')}
          </Button>
        </Stack>
        <TableContainer>
          <Table stickyHeader aria-label="sticky table">
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    style={{ minWidth: column.minWidth, background: '#FFFFFF' }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 14,
                          lineHeight: '24px',
                          fontStyle: 'normal',
                          fontWeight: 'medium',
                        }}
                      >
                        {column.label}
                      </Typography>
                      <Typography
                        sx={{
                          height: '14px',
                          width: '2px',
                          backgroundColor: '#E0E0E0',
                        }}
                      />
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.map((item, index) => (
                <TableRow hover role="checkbox" tabIndex={-1} key={index}>
                  {columns.map((column, i) => (
                    <TableCell
                      key={i}
                      align={column.align}
                      sx={{ padding: '13px', fontWeight: 'bold' }}
                    >
                      {column.render(item, index)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[]}
          component="div"
          count={pagination?.totalCount ?? 0}
          rowsPerPage={rowsPerPage ?? 0}
          sx={{
            fontSize: '12px',
            fontStyle: 'normal',
            lineHeight: '20px',
            fontWeight: 'Regular',
          }}
          page={page ?? 0}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          ActionsComponent={TablePaginationActions}
        />
      </Paper>
      {isModalOpen && <MessageModal isOpen={isModalOpen} handleClose={handleClose} id={modalId} />}

      <FileImportDialog
        dialogToggleState={isUpdateFileImportOpen}
        dialogTitle={t('contact_order.excel_import')}
        onImportFile={(file) => handleUpdateFileUpload(file)}
        closeDialog={closeUpdateFileUploadDialog}
      />
      <FileImportDialog
        dialogToggleState={isEngineFileImportOpen}
        dialogTitle={t('contact_order.order_import_engine')}
        onImportFile={(file) => handleEngineFileUpload(file)}
        closeDialog={closeEngineFileUploadDialog}
        template={'engine'}
      />
      <FileImportDialog
        dialogToggleState={isCkdPartFileImportOpen}
        dialogTitle={t('contact_order.order_import_ckd_part')}
        onImportFile={(file) => handleCkdPartFileUpload(file)}
        closeDialog={closeCkdPartFileUploadDialog}
        template={'ckd_part'}
      />
    </>
  )
}
